import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/analysis-card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Trash2, Copy, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useUrlMappingsQuery, callUrlMapper } from "@/api/vercel";
import { Spinner } from "@/components/ui/spinner";
import { logger } from "@/utils/logger";

function EditableMapping({ mapping, onSave, onCancel }) {
  const [shortCode, setShortCode] = useState(mapping.urlInput);
  const [destination, setDestination] = useState(mapping.urlDestination);

  const handleSave = () => {
    if (!shortCode.trim() || !destination.trim()) return;
    onSave(mapping.id, shortCode, destination);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-medium mb-2 block">Short Code</label>
        <Input value={shortCode} onChange={(e) => setShortCode(e.target.value)} />
      </div>
      <div>
        <label className="text-sm font-medium mb-2 block">URL Destination</label>
        <Input value={destination} onChange={(e) => setDestination(e.target.value)} />
      </div>
      <div className="flex gap-2">
        <Button onClick={handleSave} size="sm">Save</Button>
        <Button onClick={onCancel} variant="outline" size="sm">Cancel</Button>
      </div>
    </div>
  );
}

export default function UrlMappings() {
  const { toast } = useToast();
  const { data, isLoading, refetch } = useUrlMappingsQuery();
  const urlMappings = data?.data || [];
  const navigate = useNavigate();

  const [newShortCode, setNewShortCode] = useState("");
  const [newDestination, setNewDestination] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const blockedHosts = ["dev.lexi.tips", "staging.lexi.tips", "lexi.tips"];
    const currentHost = typeof window !== "undefined" ? window.location.hostname : "";
    logger.log(window.location.hostname)
    if (blockedHosts.includes(currentHost)) {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  const handleAdd = async () => {
    if (!newShortCode.trim() || !newDestination.trim()) {
      toast({ title: "Error", description: "Please fill in both fields", variant: "destructive" });
      return;
    }
    setSaving(true);
    const res = await callUrlMapper({ action: "insert", urlInput: newShortCode.trim(), urlDestination: newDestination.trim() });
    if (res?.success) {
      setNewShortCode("");
      setNewDestination("");
      toast({ title: "Success", description: "URL mapping added successfully" });
      refetch();
    } else {
      toast({ title: "Error", description: "Failed to add mapping", variant: "destructive" });
    }
    setSaving(false);
  };

  const handleEdit = (id) => {
    setEditingId(editingId === id ? null : id);
  };

  const handleSave = async (id, shortCode, destination) => {
    setSaving(true);
    const res = await callUrlMapper({ action: "update", urlInput: shortCode.trim(), urlDestination: destination.trim() });
    if (res?.success) {
      setEditingId(null);
      toast({ title: "Success", description: "URL mapping updated successfully" });
      refetch();
    } else {
      toast({ title: "Error", description: "Failed to update mapping", variant: "destructive" });
    }
    setSaving(false);
  };

  const handleDelete = async (id, urlInput) => {
    setSaving(true);
    const res = await callUrlMapper({ action: "delete", urlInput });
    if (res?.success) {
      toast({ title: "Success", description: "URL mapping deleted successfully" });
      refetch();
    } else {
      toast({ title: "Error", description: "Failed to delete mapping", variant: "destructive" });
    }
    setSaving(false);
  };

  const handleCopy = async (destination, id) => {
    try {
      await navigator.clipboard.writeText(destination);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
      toast({ title: "Copied!", description: "URL destination copied to clipboard" });
    } catch (err) {
      toast({ title: "Error", description: "Failed to copy to clipboard", variant: "destructive" });
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span>URL Mappings</span>
          <Badge variant="secondary">{isLoading ? "..." : urlMappings.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {(isLoading || saving) && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner size="small" />
            <span>{isLoading ? "Loading mappings..." : "Saving..."}</span>
          </div>
        )}
        {/* Add new mapping */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
          <div>
            <label className="text-sm font-medium mb-2 block">Short Code</label>
            <Input placeholder="e.g., fb" value={newShortCode} onChange={(e) => setNewShortCode(e.target.value)} disabled={saving} />
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">URL Destination</label>
            <Input placeholder="e.g., utm_source=facebook&utm_medium=social..." value={newDestination} onChange={(e) => setNewDestination(e.target.value)} disabled={saving} />
          </div>
          <div className="flex items-end">
            <Button onClick={handleAdd} className="w-full" disabled={saving}>
              <Plus className="w-4 h-4 mr-2" />
              {saving ? "Working..." : "Add"}
            </Button>
          </div>
        </div>

        {/* Existing mappings */}
        <div className="space-y-4">
          {urlMappings.map((mapping) => (
            <div key={mapping.id} className="border rounded-lg p-4">
              {editingId === mapping.id ? (
                <EditableMapping mapping={mapping} onSave={handleSave} onCancel={() => setEditingId(null)} />
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div>
                        <span className="text-sm text-muted-foreground">URL INPUT:</span>
                        <div className="font-mono bg-secondary px-2 py-1 rounded text-sm mt-1">{mapping.urlInput}</div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleEdit(mapping.id)} disabled={saving}>
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDelete(mapping.id, mapping.urlInput)} disabled={saving}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <span className="text-sm text-muted-foreground">URL DESTINATION:</span>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="font-mono bg-secondary px-2 py-1 rounded text-sm flex-1 break-all">{mapping.urlDestination}</div>
                      <Button size="sm" variant="ghost" onClick={() => handleCopy(mapping.urlDestination, mapping.id)}>
                        {copiedId === mapping.id ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {!isLoading && urlMappings.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">No URL mappings yet. Add one above to get started.</div>
        )}
      </CardContent>
    </Card>
  );
}


